// Normalización de teléfonos para los botones de WhatsApp (wa.me) y los links tel:.
//
// WhatsApp identifica las cuentas argentinas como 549 + característica + número, sin el 0 de larga
// distancia ni el 15 de celular: `011 15-3148-3597` → `5491131483597`. Si el número no se puede leer
// con seguridad, las funciones devuelven `null` y el botón no se muestra: un wa.me mal armado lleva a
// «El número de teléfono compartido a través de la dirección URL no es válido».
//
// Los teléfonos llegan de dos fuentes:
// - Directorio (Google vía catwoman): casi siempre en formato nacional, `02215080329` (fijo) o
//   `0111531483597` (celular, con 15), o internacional `+5491121139356`. Google escribe los celulares
//   con 15 o con 9, así que sin ninguna de las dos marcas el número se toma como fijo y no hay WhatsApp.
// - Negocios de Ágora (`vendor.user.phone`): lo cargan como su WhatsApp, casi siempre `54` + 10 dígitos
//   sin el 9 (`543547659311`). Ahí se usa `assumeMobile: true`.

// Características de 3 dígitos (sin el 0). La de 2 es solo 11 (AMBA); el resto son de 4 dígitos.
const AR_AREA_CODES_3 = new Set([
  '220', '221', '223', '230', '236', '237', '249', '260', '261', '263', '264', '266', '280', '291',
  '294', '297', '298', '299', '336', '341', '342', '343', '345', '348', '351', '353', '358', '362',
  '364', '370', '376', '379', '380', '381', '383', '385', '387', '388'
]);

// Números sin geografía: gratuitos (0800), costo compartido (0810), servicios (0600, 0610).
const AR_NON_GEOGRAPHIC = /^(800|810|600|610)/;

/** Largo de la característica según su prefijo: 2 (11), 3 (lista) o 4. */
function arAreaLength(national) {
  if (national.startsWith('11')) return 2;
  if (AR_AREA_CODES_3.has(national.slice(0, 3))) return 3;
  return 4;
}

const allSameDigit = (digits) => /^(\d)\1*$/.test(digits);

/**
 * Número nacional argentino de 10 dígitos válido: empieza con 1 (solo 11), 2 o 3, y el abonado no es
 * relleno (`00000000`) ni empieza con 0 o 1 (lo que queda de un 0 o un 15 mal puestos).
 */
function isValidArNational(national) {
  if (!/^\d{10}$/.test(national)) return false;
  if (!/^(11|[23])/.test(national)) return false;
  const subscriber = national.slice(arAreaLength(national));
  return !allSameDigit(subscriber) && !/^[01]/.test(subscriber);
}

/**
 * Saca el 15 de celular que va después de la característica (`351 15 2002660` → `3512002660`).
 * Prueba primero el largo de característica esperado y después los otros (hay de 4 dígitos que
 * empiezan como una de 3, p. ej. 2202). Devuelve `null` si no encuentra un 15 en un lugar válido.
 */
function stripMobile15(digits12) {
  const expected = arAreaLength(digits12);
  for (const len of [expected, 2, 3, 4].filter((l, i, all) => all.indexOf(l) === i)) {
    if (digits12.slice(len, len + 2) !== '15') continue;
    const national = digits12.slice(0, len) + digits12.slice(len + 2);
    if (isValidArNational(national)) return national;
  }
  return null;
}

/** `rest` = lo que sigue al 54 o al 0 de larga distancia. */
function parseArgentine(rest, { assumeMobile }) {
  let digits = rest.replace(/^0/, '');
  if (AR_NON_GEOGRAPHIC.test(digits)) {
    // Solo se marcan desde Argentina y con el 0 adelante: no hay E.164 ni WhatsApp.
    return digits.length === 10 ? { country: 'AR', type: 'non_geographic', e164: null, dial: `0${digits}` } : null;
  }

  let mobile = false;
  // 9 de celular internacional (`+54 9 11 …`): el número nacional no empieza nunca con 9.
  if (digits.startsWith('9') && (digits.length === 11 || digits.length === 13)) {
    digits = digits.slice(1);
    mobile = true;
  }
  if (digits.length === 12) {
    const national = stripMobile15(digits);
    if (!national) return null;
    digits = national;
    mobile = true;
  }
  if (!isValidArNational(digits)) return null;
  const type = mobile ? 'mobile' : assumeMobile ? 'mobile' : 'landline';
  return { country: 'AR', type, e164: type === 'mobile' ? `+549${digits}` : `+54${digits}` };
}

/** Uruguay: celulares 09X XXX XXX (8 dígitos sin el 0, empiezan con 9); fijos de 8 con 2 o 4. */
function parseUruguayan(rest) {
  const digits = rest.replace(/^0/, '');
  if (!/^\d{8}$/.test(digits) || allSameDigit(digits.slice(1))) return null;
  if (digits.startsWith('9')) return { country: 'UY', type: 'mobile', e164: `+598${digits}` };
  if (/^[24]/.test(digits)) return { country: 'UY', type: 'landline', e164: `+598${digits}` };
  return null;
}

/** Chile: celulares 9 XXXX XXXX; fijos de 9 dígitos con otra cabecera. */
function parseChilean(rest) {
  if (!/^\d{9}$/.test(rest) || allSameDigit(rest.slice(1))) return null;
  return { country: 'CL', type: rest.startsWith('9') ? 'mobile' : 'landline', e164: `+56${rest}` };
}

/**
 * Lee un teléfono de Argentina, Uruguay o Chile.
 * @param {string} raw lo que haya en la base (`+54 9 11 …`, `0221 15-…`, `543547659311`…)
 * @param {{ assumeMobile?: boolean, country?: string }} options `assumeMobile`: un número argentino
 *   sin 9 ni 15 se toma como celular (teléfonos que cargó el negocio como su WhatsApp). `country`:
 *   'Uruguay'/'UY' para leer números uruguayos sin código de país (`099 123 456`).
 * @returns {{ country: 'AR'|'UY'|'CL', type: 'mobile'|'landline'|'non_geographic', e164: string|null,
 *   dial?: string } | null} `dial` solo en los 0800/0810, que no tienen E.164.
 */
export function parsePhone(raw, { assumeMobile = false, country } = {}) {
  if (raw == null) return null;
  const text = String(raw).trim();
  const digits = text.replace(/\D/g, '');
  if (digits.length < 8 || digits.length > 15) return null;

  const isUruguay = /^(uy|uruguay)$/i.test(country || '');
  if (digits.startsWith('598')) return parseUruguayan(digits.slice(3));
  if (digits.startsWith('56') && digits.length === 11) return parseChilean(digits.slice(2));
  if (digits.startsWith('54')) return parseArgentine(digits.slice(2), { assumeMobile });
  // Con «+» y otro código de país (España, México…) no sabemos leerlo.
  if (text.startsWith('+') || digits.startsWith('00')) return null;
  if (isUruguay) return parseUruguayan(digits);
  if (digits.startsWith('0')) return parseArgentine(digits, { assumeMobile });
  // Sin 0 ni 54: `3512081998`, `91121848038` o `1131483597`.
  if (/^(9?(11|[23]))/.test(digits)) return parseArgentine(digits, { assumeMobile });
  return null;
}

/** Número para `https://wa.me/{número}` (solo dígitos) o `null` si no es un celular confiable. */
export function whatsappNumber(raw, options) {
  const parsed = parsePhone(raw, options);
  return parsed?.type === 'mobile' && parsed.e164 ? parsed.e164.slice(1) : null;
}

/** `https://wa.me/549…` o `null` (no mostrar el botón). */
export function whatsappUrl(raw, options) {
  const number = whatsappNumber(raw, options);
  return number ? `https://wa.me/${number}` : null;
}

/**
 * Link `tel:` que funciona desde cualquier país: E.164 si el número se pudo leer; si no, los dígitos
 * tal cual (con «+» si lo traía). Los 0800 se marcan como están. `null` si no hay número.
 */
export function telHref(raw, options) {
  if (raw == null || String(raw).trim() === '') return null;
  const parsed = parsePhone(raw, options);
  if (parsed?.e164) return `tel:${parsed.e164}`;
  if (parsed?.dial) return `tel:${parsed.dial}`;
  const text = String(raw).trim();
  const digits = text.replace(/\D/g, '');
  if (!digits) return null;
  return `tel:${text.startsWith('+') ? '+' : ''}${digits}`;
}

/**
 * Cómo mostrar el número. Los argentinos en formato nacional legible, como se marcan desde el país
 * (`0341 15 363-6524`, `0221 508-0329`, `0800 345-1250`). Los de Uruguay y Chile en formato
 * internacional agrupado (`+598 91 721 721`, `+598 2204 8320`, `+56 9 2187 9699`, `+56 2 2345 6789`):
 * quien mira el marketplace suele estar en Argentina y así se marcan desde acá. Lo que no se puede
 * leer sale tal cual llegó.
 */
export function formatPhoneDisplay(raw, options) {
  if (raw == null) return '';
  const text = String(raw).trim();
  const parsed = parsePhone(raw, options);
  if (!parsed) return text;
  const group = (sub) => (sub.length > 4 ? `${sub.slice(0, -4)}-${sub.slice(-4)}` : sub);
  if (parsed.type === 'non_geographic') return `${parsed.dial.slice(0, 4)} ${group(parsed.dial.slice(4))}`;
  if (parsed.country === 'AR') {
    const national = parsed.e164.replace(parsed.type === 'mobile' ? /^\+549/ : /^\+54/, '');
    const areaLength = arAreaLength(national);
    const area = `0${national.slice(0, areaLength)}`;
    const sub = group(national.slice(areaLength));
    return parsed.type === 'mobile' ? `${area} 15 ${sub}` : `${area} ${sub}`;
  }
  if (parsed.country === 'UY') {
    const n = parsed.e164.slice(4); // 8 dígitos
    return parsed.type === 'mobile'
      ? `+598 ${n.slice(0, 2)} ${n.slice(2, 5)} ${n.slice(5)}`
      : `+598 ${n.slice(0, 4)} ${n.slice(4)}`;
  }
  if (parsed.country === 'CL') {
    const n = parsed.e164.slice(3); // 9 dígitos
    // Celulares (9) y Santiago (2): un dígito de prefijo; el resto de las regiones, dos.
    return /^[92]/.test(n)
      ? `+56 ${n[0]} ${n.slice(1, 5)} ${n.slice(5)}`
      : `+56 ${n.slice(0, 2)} ${n.slice(2, 5)} ${n.slice(5)}`;
  }
  return parsed.e164;
}
